import React, { FC, useMemo, useState } from 'react';
import SidebarLayout from '../../common/SidebarLayout';
import { AnalyticsSidebarProps } from './AnalyticsSidebarProps';
import { Divider, Stack } from '@mui/material';
import Button from '@ingka/button';
import {
  ANALYTICS_PRECONFIGURED_TIME_RANGES,
  getDefaultDate,
  getDefaultDateIndex,
} from './utils';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { enGB } from 'date-fns/locale';
import RadioButtonGroup from '@ingka/radio-button-group';

export const AnalyticsSidebar: FC<AnalyticsSidebarProps> = ({
  handleDatePeriodChange,
  handleGroupingMethodChange,
  groupingMethod,
  isLoading = false,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [preconfiguredDateIndex, setPreconfiguredDateIndex] = useState<number>(
    getDefaultDateIndex(),
  );
  const [startDate, setStartDate] = useState<Date | null>(
    getDefaultDate().getStart(),
  );
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  useMemo(() => {
    if (preconfiguredDateIndex < 0) {
      return;
    }

    const start =
      ANALYTICS_PRECONFIGURED_TIME_RANGES[preconfiguredDateIndex].getStart();
    const end = new Date();
    setStartDate(start);
    setEndDate(end);
    handleDatePeriodChange(start, end);
  }, [preconfiguredDateIndex]);

  const pickCustomDate = () => {
    setPreconfiguredDateIndex(-1);
    handleDatePeriodChange(startDate, endDate);
  };

  return (
    <SidebarLayout
      mainItem={() => (
        <Stack direction={'column'} spacing={2}>
          {drawerOpen && (
            <>
              <Stack direction="column" spacing={2}>
                {handleGroupingMethodChange && (
                  <>
                    <RadioButtonGroup
                      list={[
                        {
                          id: 'analytics-grouping-day',
                          label: 'Day',
                          checked: groupingMethod === 'day',
                          onClick: () => {
                            handleGroupingMethodChange('day');
                          },
                        },
                        {
                          id: 'analytics-grouping-month',
                          label: 'Month',
                          checked: groupingMethod === 'month',
                          onClick: () => {
                            handleGroupingMethodChange('month');
                          },
                        },
                      ]}
                      name="Grouping Method"
                    />

                    <Divider sx={{ mx: 3 }} />
                  </>
                )}

                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={enGB}
                >
                  <DatePicker
                    disabled={isLoading}
                    label="Start Date"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    maxDate={endDate}
                  />
                  <DatePicker
                    disabled={isLoading}
                    label="End Date"
                    value={endDate ? endDate : null}
                    onChange={(newValue) => setEndDate(newValue)}
                    maxDate={new Date()}
                    slotProps={{
                      actionBar: {
                        actions: ['today', 'accept'],
                      },
                    }}
                  />
                  <Button
                    disabled={isLoading}
                    type={preconfiguredDateIndex < 0 ? 'primary' : 'secondary'}
                    onClick={pickCustomDate}
                  >
                    Apply Time Range
                  </Button>
                </LocalizationProvider>

                <Divider sx={{ mx: 3 }} />

                {ANALYTICS_PRECONFIGURED_TIME_RANGES.map((e, index) => {
                  return (
                    <Button
                      disabled={isLoading}
                      type={
                        index === preconfiguredDateIndex
                          ? 'primary'
                          : 'secondary'
                      }
                      onClick={() => setPreconfiguredDateIndex(index)}
                    >
                      {e.label}
                    </Button>
                  );
                })}
              </Stack>
            </>
          )}
        </Stack>
      )}
      drawerOpen={drawerOpen}
      setDrawerOpen={setDrawerOpen}
    />
  );
};
